import { type RefObject, useEffect, useRef } from "react";

/** How close to the bottom (px) still counts as "reading the newest message". */
const PIN_SLACK_PX = 80;

/**
 * Keep the newest message in view while the thread changes size UNDER the reader: the viewport
 * (window resize, a phone rotating, the mobile keyboard, the expand toggle) and the content itself
 * (a poster / picture / video frame / the demo text bundle / a font landing late). The tab's own
 * scroll effect only watches the content for ~2 s after a message arrives, so growth after that
 * (a slow network, a cold dev server) used to leave the reader above the newest message.
 *
 * Whether the reader was pinned to the bottom is tracked HERE from scroll events, ignoring a
 * scroll event that arrives together with a size change: the browser may nudge `scrollTop` while
 * it reflows, and that must not read as "the reader scrolled up". A reader who really scrolled up
 * is left where they are. `canRepin` adds the tab's own holds (unread divider, pin lock).
 *
 * `rowsKey` identifies what the thread shows (message count, typing row). Growth that comes with a
 * change of it is a new message: the tab's own effect decides where that lands (glide to a short
 * bubble, peek at a long one), so it is left alone here. Growth with the SAME rows is the content
 * settling, and is followed for as long as the reader stays at the bottom (no time window).
 */
export function useRepinOnResize(
  scrollRef: RefObject<HTMLElement | null>,
  canRepin: () => boolean,
  rowsKey: string | number,
): void {
  const canRepinRef = useRef(canRepin);
  canRepinRef.current = canRepin;
  const rowsKeyRef = useRef(rowsKey);
  rowsKeyRef.current = rowsKey;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const content = el.firstElementChild instanceof HTMLElement ? el.firstElementChild : null;
    let lastH = el.clientHeight;
    let lastW = el.clientWidth;
    let lastContentH = content?.offsetHeight ?? 0;
    let lastRows = rowsKeyRef.current;
    let pinned = true; // a thread opens on the newest message
    const unchanged = () =>
      el.clientHeight === lastH &&
      el.clientWidth === lastW &&
      (content?.offsetHeight ?? 0) === lastContentH;
    const onScroll = () => {
      // A scroll that came with a size change is the browser re-laying-out, not the reader.
      if (!unchanged()) return;
      pinned = el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_SLACK_PX;
    };
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      const w = el.clientWidth;
      const contentH = content?.offsetHeight ?? 0;
      const rows = rowsKeyRef.current;
      const viewportChanged = h !== lastH || w !== lastW;
      const contentSettled = contentH !== lastContentH && rows === lastRows;
      lastH = h;
      lastW = w;
      lastContentH = contentH;
      lastRows = rows;
      if (!viewportChanged && !contentSettled) return;
      if (pinned && canRepinRef.current()) el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    });
    el.addEventListener("scroll", onScroll, { passive: true });
    ro.observe(el);
    if (content) ro.observe(content);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [scrollRef]);
}
